import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  CartesianGrid,
} from "recharts";

const Stat = ({ label, value, hint }) => (
  <div
    className="border p-6 fade-up"
    style={{ background: "var(--rs-surface)", borderColor: "var(--rs-border)" }}
  >
    <div className="overline mb-3">{label}</div>
    <div className="font-serif-display text-4xl text-bone">{value}</div>
    {hint && <div className="text-xs text-muted-bone mt-2">{hint}</div>}
  </div>
);

const chartTooltip = {
  contentStyle: {
    background: "#110e0e",
    border: "1px solid rgba(212,175,55,0.3)",
    borderRadius: 0,
    color: "#f5f2eb",
    fontSize: 12,
  },
};

const downloadCsv = async (kind) => {
  try {
    const res = await api.get(`/admin/export/${kind}.csv`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ramshack_${kind}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Export failed");
  }
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);

  useEffect(() => {
    api.get(`/admin/analytics/overview?days=${days}`).then((r) => setAnalytics(r.data)).catch(() => {});
  }, [days]);

  if (!stats) return <div className="text-muted-bone">Loading…</div>;

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-12">
        <div>
          <div className="overline mb-3">House Overview · Drop-ship</div>
          <h1 className="font-serif-display text-5xl text-bone">Tonight's numbers.</h1>
        </div>
        <div className="flex gap-3">
          {["orders", "customers", "products"].map((kind) => (
            <button
              key={kind}
              data-testid={`export-${kind}-btn`}
              onClick={() => downloadCsv(kind)}
              className="flex items-center gap-2 border px-4 py-2 text-xs uppercase tracking-widest text-muted-bone hover:text-bone transition"
              style={{ borderColor: "var(--rs-border)" }}
            >
              <Download size={13} /> {kind}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        <Stat label="Revenue" value={`$${stats.revenue.toFixed(2)}`} hint="Net of cancellations" />
        <Stat label="Supplier cost" value={`$${(stats.supplier_cost || 0).toFixed(2)}`} hint="Cost of goods (set per product)" />
        <Stat label="Gross margin" value={`$${(stats.margin || 0).toFixed(2)}`} hint={stats.revenue > 0 ? `${((stats.margin / stats.revenue) * 100).toFixed(1)}% margin` : ""} />
        <Stat label="Customers" value={stats.total_users} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
        <Stat label="Awaiting forward" value={stats.awaiting_forward || 0} hint="Place these on supplier" />
        <Stat label="In fulfilment" value={stats.in_fulfillment || 0} hint="Supplier processing" />
        <Stat label="Shipped" value={stats.shipped_orders || 0} hint="On the way" />
      </div>

      {/* Analytics */}
      <div className="flex items-center justify-between mb-4">
        <div className="overline">Business intelligence</div>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`analytics-range-${d}`}
              onClick={() => setDays(d)}
              className={`px-3 py-1 text-xs border ${days === d ? "text-bone" : "text-muted-bone"}`}
              style={days === d ? { background: "var(--rs-oxblood)", borderColor: "var(--rs-oxblood)" } : { borderColor: "var(--rs-border)" }}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          <div className="border p-6" style={{ background: "var(--rs-surface)", borderColor: "var(--rs-border)" }} data-testid="revenue-chart">
            <div className="overline mb-4">Revenue — last {analytics.days} days (AOV ${analytics.aov.toFixed(2)})</div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={analytics.revenue_by_day}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#d4af37" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#d4af37" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#8a8578", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" />
                <YAxis tick={{ fill: "#8a8578", fontSize: 10 }} width={40} />
                <Tooltip {...chartTooltip} />
                <Area type="monotone" dataKey="revenue" stroke="#d4af37" fill="url(#rev)" strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="border p-6" style={{ background: "var(--rs-surface)", borderColor: "var(--rs-border)" }} data-testid="category-chart">
            <div className="overline mb-4">Revenue by category</div>
            {analytics.category_revenue.length === 0 ? (
              <div className="text-muted-bone text-sm h-[220px] flex items-center justify-center">No sales in this window yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={analytics.category_revenue}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="category" tick={{ fill: "#8a8578", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#8a8578", fontSize: 10 }} width={40} />
                  <Tooltip {...chartTooltip} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Bar dataKey="revenue" fill="#6f1d1b" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="border p-6" style={{ background: "var(--rs-surface)", borderColor: "var(--rs-border)" }} data-testid="top-products">
            <div className="overline mb-4">Top products</div>
            {analytics.top_products.length === 0 ? (
              <div className="text-muted-bone text-sm">No sales in this window yet.</div>
            ) : (
              <div className="space-y-3">
                {analytics.top_products.map((p) => (
                  <div key={p.product_id} className="flex justify-between text-sm">
                    <span className="text-muted-bone">{p.name} <span className="text-xs">×{p.units}</span></span>
                    <span className="text-bone">${p.revenue.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border p-6" style={{ background: "var(--rs-surface)", borderColor: "var(--rs-border)" }} data-testid="cart-funnel">
            <div className="overline mb-4">Cart funnel — last {analytics.days} days</div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-bone">Carts tracked</span><span className="text-bone">{analytics.carts_tracked}</span></div>
              <div className="flex justify-between"><span className="text-muted-bone">Converted</span><span className="text-bone">{analytics.carts_converted}</span></div>
              <div className="flex justify-between"><span className="text-muted-bone">Conversion rate</span>
                <span className="text-bone">{analytics.carts_tracked > 0 ? `${((analytics.carts_converted / analytics.carts_tracked) * 100).toFixed(1)}%` : "—"}</span>
              </div>
              <div className="flex justify-between"><span className="text-muted-bone">Orders in window</span><span className="text-bone">{analytics.orders_count}</span></div>
              <div className="flex justify-between"><span className="text-muted-bone">Window margin</span><span className="text-bone">${analytics.margin.toFixed(2)}</span></div>
            </div>
          </div>
        </div>
      )}

      <div className="overline mb-4">Recent orders</div>
      {stats.recent_orders.length === 0 ? (
        <div
          className="border p-10 text-center text-muted-bone"
          style={{ background: "var(--rs-surface)", borderColor: "var(--rs-border)" }}
        >
          No orders yet. The house is quiet.
        </div>
      ) : (
        <div className="border" style={{ borderColor: "var(--rs-border)" }}>
          <table className="w-full">
            <thead>
              <tr className="text-xs uppercase tracking-[0.22em] text-muted-bone">
                <th className="text-left px-6 py-4">Order</th>
                <th className="text-left px-6 py-4">Customer</th>
                <th className="text-left px-6 py-4">Items</th>
                <th className="text-left px-6 py-4">Amount</th>
                <th className="text-left px-6 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_orders.map((o) => (
                <tr
                  key={o.order_id}
                  className="border-t text-sm"
                  style={{ borderColor: "var(--rs-border)" }}
                >
                  <td className="px-6 py-4 text-bone font-mono text-xs">{o.order_id.slice(-10)}</td>
                  <td className="px-6 py-4 text-muted-bone">{o.user_email || "guest"}</td>
                  <td className="px-6 py-4 text-muted-bone">{o.items.length}</td>
                  <td className="px-6 py-4 text-bone">${o.amount.toFixed(2)}</td>
                  <td className="px-6 py-4">
                    <span
                      className="px-2 py-1 text-[10px] uppercase tracking-widest"
                      style={{ background: "var(--rs-oxblood)" }}
                    >
                      {o.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
